
import type { BlockRendererProps } from '@/lib/email-builder/block-registry';
import { parseBlockConfiguration } from '@/types/email-blocks';
import type { FooterBlockConfiguration } from '@/types/block-configurations';

export function FooterBlock({ block, isSelected, isHovered, onSelect, onHover }: BlockRendererProps) {
  const config = parseBlockConfiguration(block) as FooterBlockConfiguration;

  const justify =
    config.alignment?.horizontal === 'left'
      ? 'justify-start'
      : config.alignment?.horizontal === 'right'
        ? 'justify-end'
        : 'justify-center';

  return (
    <div
      onClick={(e) => { e.stopPropagation(); onSelect(block.id); }}
      onMouseEnter={(e) => { e.stopPropagation(); onHover(block.id); }}
      onMouseLeave={() => onHover(null)}
      className={`
        lw-block-footer cursor-pointer rounded transition-all
        ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
        ${isHovered && !isSelected ? 'ring-1 ring-muted-foreground/50' : ''}
      `}
      style={{
        backgroundColor: config.background?.color || '#F5F5F5',
        paddingTop: `${config.padding?.top ?? 30}px`,
        paddingRight: `${config.padding?.right ?? 20}px`,
        paddingBottom: `${config.padding?.bottom ?? 30}px`,
        paddingLeft: `${config.padding?.left ?? 20}px`,
        textAlign: config.alignment?.horizontal || 'center',
        fontSize: `${config.typography?.fontSize ?? 12}px`,
        color: config.typography?.color || '#666666',
        fontFamily: config.typography?.fontFamily || 'Arial',
      }}
    >
      {/* Nav links — joined with " | " like FooterBlockRenderer.cs */}
      {config.links && config.links.length > 0 && (
        <div className="mb-3">
          {config.links.map((link, i) => (
            <span key={i}>
              <a href={link.url} onClick={(e) => e.preventDefault()} className="underline" style={{ color: config.typography?.color || '#666666' }}>
                {link.text}
              </a>
              {i < (config.links?.length ?? 0) - 1 && <span> | </span>}
            </span>
          ))}
        </div>
      )}

      {/* Social links */}
      {config.socialLinks && config.socialLinks.length > 0 && (
        <div className={`mb-3 flex flex-wrap gap-2 ${justify}`}>
          {config.socialLinks.map((sl, i) => (
            <span key={i} className="inline-block rounded-full bg-gray-300 px-2 py-1 text-xs capitalize">
              {sl.platform}
            </span>
          ))}
        </div>
      )}

      {config.companyName && <div className="font-medium">{config.companyName}</div>}
      {config.address && <div className="mt-1">{config.address}</div>}
      {config.legalText && <div className="mt-1">{config.legalText}</div>}

      {config.showUnsubscribe && (
        <div className="mt-2">
          <a href={config.unsubscribeUrl || '#'} onClick={(e) => e.preventDefault()} className="underline" style={{ color: config.typography?.color || '#666666' }}>
            Unsubscribe
          </a>
        </div>
      )}

      {!config.companyName && !config.address && !config.legalText && (
        <p className="text-muted-foreground select-none">Configure footer content in properties</p>
      )}
    </div>
  );
}
